import CacheService from "../cache/cache_service";
import EnvService from "../env";
import BroadcastService from "../broadcast";
import {createUUId} from "../utils/utils";
import LocalStorageService from "../cache/local_storage_service";

/**
 * How a 401 should be treated for the request that received it.
 * - pause: the token in use right now was rejected. Block every tab.
 * - retry: the failure belongs to a token that login already replaced. Retry once, without opening the dialog.
 * - ignore: that retry also failed for a replaced token. Return the error and leave the current session in place.
 */
export type AuthFailureAction = 'pause' | 'retry' | 'ignore';

export type AuthRequestStamp = {
    generation: number;
    authorization: string | null;
};

export default class AuthBlockerService {
    private static _authPromise: Promise<void> | null = null;
    private static _resolveAuth: (() => void) | null = null;
    private static _rejectAuth: (() => void) | null = null;
    private static _isPaused = false;
    private static owner = `auth-blocker-service-${createUUId()}`;
    /** Login id of this document. Shared with other tabs through localStorage. */
    private static generation = 0;
    /** Token this document has already adopted for `generation`. */
    private static acceptedToken: string | null = null;

    static get currentGeneration() {
        return this.generation;
    }

    /**
     * Detects if the current context is an iframe, which is commonly used for authentication flows.
     * This can be used to conditionally disable the auth blocker in iframes to prevent issues with nested blocking.
     */
    static get isInAuthIframe() {
        if (typeof window === 'undefined') {
            return false;
        }
        try {
            return window.self !== window.top;
        } catch (e) {
            return true; // If we get a cross-origin error, we are in an iframe
        }
    }


    /**
     * Global listener for cross-tab or iframe sync
     */
    static attachSyncListener() {
        if (!EnvService.DEV_useAuthBlocker)
            return;

        if (this.isInAuthIframe) {
            return;
        }
        AuthBlockerService.syncGeneration();
        BroadcastService.postMessage(BroadcastService.channelNames.authBlockerService, {type: 'request-status', owner: AuthBlockerService.owner});
        BroadcastService.addEventListener(BroadcastService.channelNames.authBlockerService, 'message', (data) => {
            if (data?.type === 'login-success') {
                AuthBlockerService.syncGeneration();
                if (AuthBlockerService.isPaused) {
                    AuthBlockerService.resume();
                }
            }
            if (data?.type === 'request-status' && data.owner !== AuthBlockerService.owner) {
                if (AuthBlockerService.isPaused) {
                    BroadcastService.postMessage(BroadcastService.channelNames.authBlockerService, {
                        type: 'status-update',
                        isPaused: true,
                        owner: AuthBlockerService.owner,
                        generation: AuthBlockerService.currentGeneration,
                    });
                }
            }
            if (data?.type === 'status-update' && data.owner !== AuthBlockerService.owner) {
                AuthBlockerService.syncGeneration();
                const announcedGeneration = typeof data.generation === 'number' ? data.generation : null;
                if (announcedGeneration != null && announcedGeneration < AuthBlockerService.currentGeneration) {
                    return;
                }
                if (data.isPaused && !AuthBlockerService.isPaused) {
                    AuthBlockerService.pause(false);
                }
            }
        });
    }


    static get isPaused() {
        return this._isPaused;
    }

    /**
     * Pauses the API executions and returns a promise that resolves when the user authenticates.
     */
    static pause(triggerBroadcast = true): [Promise<void>, boolean] {
        if (!EnvService.DEV_useAuthBlocker)
            return [Promise.resolve(), true];

        this.syncGeneration();
        if (this._authPromise) {
            return [this._authPromise, false];
        }
        this._isPaused = true;
        this._authPromise = new Promise((resolve, reject) => {
            this._resolveAuth = resolve;
            this._rejectAuth = reject;
        });
        BroadcastService.postMessage(BroadcastService.channelNames.authBlockerDialog, {type: "auth-halt-started"});

        if (triggerBroadcast) {
            BroadcastService.postMessage(BroadcastService.channelNames.authBlockerService, {
                type: 'status-update',
                isPaused: true,
                owner: AuthBlockerService.owner,
                generation: AuthBlockerService.currentGeneration,
            });
        }

        return [this._authPromise, false];
    }

    /**
     * Resumes API executions after successful authentication.
     */
    static resume() {
        if (!EnvService.DEV_useAuthBlocker)
            return;

        if (this._resolveAuth) {
            this._resolveAuth();
        }
        this.reset();
    }

    /**
     * Cancels the pause and rejects the pending promise.
     */
    static cancel() {
        if (!EnvService.DEV_useAuthBlocker)
            return;

        if (this._rejectAuth) {
            this._rejectAuth();
        }
        this.reset();
    }

    /**
     * Resets the internal state.
     */
    private static reset() {
        this._isPaused = false;
        this._authPromise = null;
        this._resolveAuth = null;
        this._rejectAuth = null;
        BroadcastService.postMessage(BroadcastService.channelNames.authBlockerDialog, {type: "auth-halt-ended"});
    }

    /**
     * Returns a promise if the system is currently paused or if the user is not logged in.
     * Otherwise returns a resolved promise.
     *
     * @returns A tuple where the first element is a promise that resolves when authentication is complete, and the second element is a boolean indicating whether the auth blocker is effectively disabled (either through environment settings or because we're in an iframe).
     */
    static waitForAuth(): [Promise<void>, boolean] {
        if (!EnvService.DEV_useAuthBlocker)
            return [Promise.resolve(), true];

        if (EnvService.bypassAuth) {
            return [Promise.resolve(), false];
        }
        if (this.isInAuthIframe) {
            return [Promise.resolve(), false];
        }
        if (this._isPaused && this._authPromise) {
            return [this._authPromise, false];
        }
        if (!CacheService.isLoggedIn()) {
            return this.pause();
        }
        return [Promise.resolve(), false];
    }

    /**
     * Records a newly stored token as the current login.
     * The same token does not bump the generation. A different token does, and the new id is written for other tabs.
     * @param token the operator token just written to local storage
     */
    static acceptToken(token?: string | null) {
        if (!EnvService.DEV_useAuthBlocker) {
            return this.generation;
        }
        if (!token || token === this.acceptedToken) {
            return this.generation;
        }
        const stored = this.readStoredGeneration() ?? 0;
        this.generation = Math.max(this.generation, stored) + 1;
        this.acceptedToken = token;
        LocalStorageService.set(LocalStorageService.keys.authGeneration, String(this.generation));
        return this.generation;
    }

    /**
     * Adopts the login id written by the document that stored the current token.
     * Does not increment. A tab that already moved to a newer login keeps that newer id.
     */
    static syncGeneration() {
        const token = this.readToken();
        const stored = this.readStoredGeneration();
        if (!token) {
            this.acceptedToken = null;
            if (stored != null && stored > this.generation) {
                this.generation = stored;
            }
            return;
        }
        if (stored != null && stored > this.generation) {
            this.generation = stored;
        }
        if (token !== this.acceptedToken) {
            this.acceptedToken = token;
            if (stored != null) {
                this.generation = Math.max(this.generation, stored);
            }
        }
    }

    /**
     * Reads the Authorization header that axios actually sent.
     * @param headers axios request headers, which may be a plain object or an AxiosHeaders instance
     */
    static readAuthorizationHeader(headers: unknown): string | null {
        if (!headers || typeof headers !== 'object') {
            return null;
        }
        const record = headers as { get?: (name: string) => unknown; Authorization?: unknown; authorization?: unknown };
        const raw = typeof record.get === 'function'
            ? (record.get('Authorization') ?? record.get('authorization'))
            : (record.Authorization ?? record.authorization);
        if (Array.isArray(raw)) {
            return raw[0] != null && raw[0] !== '' ? String(raw[0]) : null;
        }
        if (raw == null || raw === '') {
            return null;
        }
        return String(raw);
    }

    /**
     * Stamps an outgoing request with the login that is current at send time, and the token on that request.
     * @param headers headers about to be sent
     */
    static captureRequestStamp(headers: unknown): AuthRequestStamp {
        this.syncGeneration();
        return {
            generation: this.generation,
            authorization: this.readAuthorizationHeader(headers),
        };
    }

    /**
     * A failure is stale when it belongs to a login this document has already moved past,
     * or when the rejected token is no longer the token in storage.
     * @param requestGeneration login id captured when the request was sent
     * @param sentAuthorization Authorization header captured from the request that failed
     */
    static isStaleAuthFailure(requestGeneration: number, sentAuthorization?: string | null): boolean {
        this.syncGeneration();
        if (requestGeneration < this.generation) {
            return true;
        }
        const current = this.readToken();
        if (sentAuthorization && current && sentAuthorization !== current) {
            return true;
        }
        return false;
    }

    /**
     * Decides whether a 401 blocks the app, retries once, or is dropped.
     * A 401 for the current token still pauses. A 401 for a replaced token does not.
     * @param requestGeneration login id captured when the request was sent
     * @param sentAuthorization Authorization header captured from the request that failed
     * @param retried how many times this call has already been repeated after an auth failure
     */
    static classifyAuthFailure(requestGeneration: number, sentAuthorization: string | null | undefined, retried: number): AuthFailureAction {
        if (this.isStaleAuthFailure(requestGeneration, sentAuthorization)) {
            return retried < 1 ? 'retry' : 'ignore';
        }
        return 'pause';
    }

    /**
     * Applies `classifyAuthFailure` and, when the current token was rejected, waits for login before retrying.
     * Returns the retry result, or null when the caller should keep the original error.
     * @param options.retried how many times this call has already been repeated
     * @param options.requestGeneration login id captured when the request was sent
     * @param options.sentAuthorization Authorization header captured from the request that failed
     * @param options.retry repeats the call with whatever token is current now
     */
    static async recoverFromUnauthorized<T>(options: {
        retried: number;
        requestGeneration: number;
        sentAuthorization: string | null;
        retry: () => Promise<T>;
    }): Promise<T | null> {
        if (!EnvService.DEV_useAuthBlocker) {
            return null;
        }
        const action = this.classifyAuthFailure(options.requestGeneration, options.sentAuthorization, options.retried);
        if (action === 'retry') {
            return options.retry();
        }
        if (action === 'ignore') {
            return null;
        }
        try {
            const [pausePromise, ignore] = this.pause();
            if (!ignore) {
                await pausePromise;
                return options.retry();
            }
        } catch {
            // The pause was cancelled. The caller returns the original error.
        }
        return null;
    }

    private static readToken(): string | null {
        const token = LocalStorageService.get(LocalStorageService.keys.token);
        return token ? String(token) : null;
    }

    private static readStoredGeneration(): number | null {
        const raw = LocalStorageService.get(LocalStorageService.keys.authGeneration);
        if (raw == null || raw === '') {
            return null;
        }
        const parsed = parseInt(raw, 10);
        return Number.isFinite(parsed) ? parsed : null;
    }
}
